import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  FileSignature,
  Loader2,
  CheckCircle2,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Use a bare axios (NOT the auth-injecting api client) since this is a public endpoint
const publicApi = axios.create({ baseURL: `${BACKEND}/api` });

const W9PublicForm = () => {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Single "missing field" highlight — clears as soon as the user starts editing it.
  const [errorField, setErrorField] = useState(null);
  const fieldRefs = useRef({});
  const registerRef = (key) => (el) => {
    if (el) fieldRefs.current[key] = el;
  };
  const errorClass = (key) =>
    errorField === key
      ? "border-red-500 ring-2 ring-red-300 focus-visible:ring-red-400"
      : "";
  const flagField = (key, message) => {
    setErrorField(key);
    toast.error(message);
    const el = fieldRefs.current[key];
    if (el) {
      // Scroll the element into view, then focus the underlying input/button.
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el.matches("input, textarea, button")
        ? el
        : el.querySelector("input, textarea, button");
      setTimeout(() => focusable?.focus(), 350);
    }
  };

  const [form, setForm] = useState({
    legal_name: "",
    business_name: "",
    tax_classification: "",
    llc_classification: "",
    other_classification: "",
    exempt_payee_code: "",
    fatca_exemption_code: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    account_numbers: "",
    ssn: "",
    ein: "",
    signature: "",
    certified: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await publicApi.get(`/w9/public/${token}`);
        setMeta(res.data);
        if (res.data.already_completed) setSubmitted(true);
      } catch (err) {
        setError(err.response?.data?.detail || "Link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Always render the vendor-facing form in light theme — public users may have OS
  // dark mode but this is a tax form that needs to look professional and printable.
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    root.classList.remove("dark");
    return () => {
      if (hadDark) root.classList.add("dark");
    };
  }, []);

  const update = (field) => (e) => {
    const value = e?.target?.value !== undefined ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    if (errorField === field) setErrorField(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Front-end validation — name the specific missing field so the vendor knows what to fix.
    const requiredFields = [
      ["legal_name", "Name (line 1)"],
      ["tax_classification", "Federal tax classification (line 3)"],
      ["address", "Street address (line 5)"],
      ["city", "City"],
      ["state", "State"],
      ["zip_code", "ZIP code"],
      ["signature", "Signature"],
    ];
    for (const [key, label] of requiredFields) {
      if (!form[key] || (typeof form[key] === "string" && !form[key].trim())) {
        flagField(key, `Please fill in: ${label}`);
        return;
      }
    }
    if (!form.ssn && !form.ein) {
      flagField("ein", "Please enter either an SSN or EIN");
      return;
    }
    if (form.tax_classification === "llc" && !form.llc_classification) {
      flagField("llc_classification", "Please choose how the LLC is taxed");
      return;
    }
    if (form.tax_classification === "other" && !form.other_classification) {
      flagField("other_classification", "Please describe the 'other' classification");
      return;
    }
    if (!form.certified) {
      flagField("certified", "You must check the certification box");
      return;
    }

    setSubmitting(true);
    try {
      await publicApi.post(`/w9/public/${token}/submit`, {
        ...form,
        signature_date: new Date().toISOString(),
      });
      setSubmitted(true);
      toast.success("W-9 submitted — thank you!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-destructive/10 text-destructive mx-auto flex items-center justify-center">
              <AlertTriangle size={24} />
            </div>
            <h2 className="text-xl font-bold">Link Invalid</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground">
              Please contact the SMJ team member who sent this link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-500 mx-auto flex items-center justify-center">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Thank you!</h2>
            <p className="text-sm text-muted-foreground">
              Your W-9 has been submitted to Steele Martin Jones &amp; Company.
              You can safely close this window.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto" data-testid="w9-public-form">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-xl bg-indigo-500/10 text-indigo-500 mx-auto flex items-center justify-center mb-3">
            <FileSignature size={28} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Form W-9
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Request for Taxpayer Identification Number and Certification
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Requested by <strong>{meta.requester_name}</strong> at Steele Martin
            Jones &amp; Company on behalf of client{" "}
            <strong>{meta.client_company_name}</strong>
          </p>
        </div>

        <Card>
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={submit} className="space-y-6">
              {/* Name + Business */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    1. Name (as shown on your income tax return) *
                  </label>
                  <Input
                    ref={registerRef("legal_name")}
                    value={form.legal_name}
                    onChange={update("legal_name")}
                    placeholder="Your full legal name"
                    className={errorClass("legal_name")}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    2. Business name / disregarded entity name (if different)
                  </label>
                  <Input
                    value={form.business_name}
                    onChange={update("business_name")}
                    placeholder="Leave blank if same as line 1"
                  />
                </div>
              </div>

              {/* Tax Classification */}
              <div className="space-y-3">
                <label className="text-sm font-medium">
                  3. Federal Tax Classification *
                </label>
                <Select
                  value={form.tax_classification}
                  onValueChange={(v) => {
                    setForm({ ...form, tax_classification: v });
                    if (errorField === "tax_classification") setErrorField(null);
                  }}
                >
                  <SelectTrigger
                    ref={registerRef("tax_classification")}
                    className={errorClass("tax_classification")}
                  >
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">
                      Individual / sole proprietor / single-member LLC
                    </SelectItem>
                    <SelectItem value="c_corp">C Corporation</SelectItem>
                    <SelectItem value="s_corp">S Corporation</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="trust">Trust / estate</SelectItem>
                    <SelectItem value="llc">
                      Limited liability company (multi-member)
                    </SelectItem>
                    <SelectItem value="other">Other…</SelectItem>
                  </SelectContent>
                </Select>

                {form.tax_classification === "llc" && (
                  <div className="space-y-1.5 pl-4 border-l-2 border-indigo-500/40">
                    <label className="text-xs font-medium text-muted-foreground">
                      How is the LLC taxed? *
                    </label>
                    <Select
                      value={form.llc_classification}
                      onValueChange={(v) => {
                        setForm({ ...form, llc_classification: v });
                        if (errorField === "llc_classification") setErrorField(null);
                      }}
                    >
                      <SelectTrigger
                        ref={registerRef("llc_classification")}
                        className={errorClass("llc_classification")}
                      >
                        <SelectValue placeholder="Choose…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="C">C corporation</SelectItem>
                        <SelectItem value="S">S corporation</SelectItem>
                        <SelectItem value="P">Partnership</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.tax_classification === "other" && (
                  <div className="space-y-1.5 pl-4 border-l-2 border-indigo-500/40">
                    <Input
                      ref={registerRef("other_classification")}
                      value={form.other_classification}
                      onChange={update("other_classification")}
                      placeholder="Describe…"
                      className={errorClass("other_classification")}
                    />
                  </div>
                )}
              </div>

              {/* Exemptions */}
              <details className="border border-border rounded-lg p-3 group">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  4. Exemptions (rare — click to expand)
                </summary>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      Exempt payee code (if any)
                    </label>
                    <Input
                      value={form.exempt_payee_code}
                      onChange={update("exempt_payee_code")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      FATCA exemption code (if any)
                    </label>
                    <Input
                      value={form.fatca_exemption_code}
                      onChange={update("fatca_exemption_code")}
                    />
                  </div>
                </div>
              </details>

              {/* Address */}
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">5. Street address *</label>
                  <Input
                    ref={registerRef("address")}
                    value={form.address}
                    onChange={update("address")}
                    placeholder="Number, street, and apt or suite"
                    className={errorClass("address")}
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5 sm:col-span-1">
                    <label className="text-sm font-medium">6. City *</label>
                    <Input
                      ref={registerRef("city")}
                      value={form.city}
                      onChange={update("city")}
                      className={errorClass("city")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">State *</label>
                    <Input
                      ref={registerRef("state")}
                      value={form.state}
                      onChange={update("state")}
                      maxLength={2}
                      placeholder="2-letter"
                      className={errorClass("state")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">ZIP *</label>
                    <Input
                      ref={registerRef("zip_code")}
                      value={form.zip_code}
                      onChange={update("zip_code")}
                      className={errorClass("zip_code")}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  7. Account numbers (optional)
                </label>
                <Textarea
                  value={form.account_numbers}
                  onChange={update("account_numbers")}
                  rows={2}
                  placeholder="If requested by the requester"
                />
              </div>

              {/* TIN */}
              <div className="space-y-3 bg-muted/40 rounded-lg p-4">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-500" />
                  Part I — Taxpayer Identification Number
                </h3>
                <p className="text-xs text-muted-foreground">
                  Enter <strong>either</strong> your Social Security Number{" "}
                  <strong>OR</strong> your Employer Identification Number — not both.
                  Your TIN is encrypted as soon as you submit and only visible to
                  the SMJ staff member who requested this W-9.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                      SSN (XXX-XX-XXXX)
                    </label>
                    <Input
                      ref={registerRef("ssn")}
                      value={form.ssn}
                      onChange={update("ssn")}
                      placeholder="XXX-XX-XXXX"
                      maxLength={11}
                      autoComplete="off"
                      className={errorClass("ssn")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">
                      OR EIN (XX-XXXXXXX)
                    </label>
                    <Input
                      ref={registerRef("ein")}
                      value={form.ein}
                      onChange={update("ein")}
                      placeholder="XX-XXXXXXX"
                      maxLength={10}
                      autoComplete="off"
                      className={errorClass("ein")}
                    />
                  </div>
                </div>
              </div>

              {/* Certification */}
              <div className="space-y-3 border-t border-border pt-6">
                <h3 className="text-sm font-semibold">Part II — Certification</h3>
                <div className="text-xs text-muted-foreground space-y-2">
                  <p>Under penalties of perjury, I certify that:</p>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li>The number shown on this form is my correct taxpayer identification number, and</li>
                    <li>I am not subject to backup withholding, and</li>
                    <li>I am a U.S. citizen or other U.S. person, and</li>
                    <li>The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.</li>
                  </ol>
                </div>
                <div
                  ref={registerRef("certified")}
                  className={`flex items-start gap-2 pt-2 rounded-md ${
                    errorField === "certified"
                      ? "ring-2 ring-red-300 bg-red-50 p-2 -m-2"
                      : ""
                  }`}
                >
                  <Checkbox
                    id="cert"
                    checked={form.certified}
                    onCheckedChange={(v) => {
                      setForm({ ...form, certified: !!v });
                      if (errorField === "certified") setErrorField(null);
                    }}
                  />
                  <label
                    htmlFor="cert"
                    className="text-sm text-foreground cursor-pointer"
                  >
                    I certify, under penalties of perjury, that the above is true and correct. *
                  </label>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Signature (type your full legal name) *
                  </label>
                  <Input
                    ref={registerRef("signature")}
                    value={form.signature}
                    onChange={update("signature")}
                    placeholder="Type your full legal name"
                    className={`font-serif text-lg italic ${errorClass("signature")}`}
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-indigo-600 hover:bg-indigo-700 h-11"
                data-testid="w9-public-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting…
                  </>
                ) : (
                  "Submit W-9"
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                🔒 Encrypted end-to-end. Only {meta.requester_name} will receive your W-9.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default W9PublicForm;
